import { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Users } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { ActionButton, GhostButton } from "./Primitives";
import { toast } from "sonner";

/**
 * Full-screen modal that auto-appears when the guard portal detects a pending
 * status-check prompt. Officers pick one of three responses:
 *   • OK — I'm good        (green ✓)
 *   • Investigating        (yellow ⚠)
 *   • Need backup          (red — routes to supervisor as high-priority)
 *
 * Countdown shows how long until the prompt flips to "missed" (server-side
 * grace_expires_at). We do NOT ack automatically — a missed prompt should
 * only be caused by an unresponsive guard.
 */
export default function StatusCheckModal({ prompt, onAcknowledged }) {
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState("");
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remainingMs = prompt?.grace_expires_at
    ? new Date(prompt.grace_expires_at).getTime() - tick
    : 0;
  const remainingSec = Math.max(0, Math.round(remainingMs / 1000));
  const isCritical = remainingSec < 60;

  const ack = async (response) => {
    setBusy(response);
    try {
      await api.post(`/portal/status-check/${prompt.id}/acknowledge`, {
        response,
        note: note || null,
      });
      toast.success(response === "need_backup"
        ? "Backup requested. Supervisor is being alerted."
        : "Status confirmed. Stay safe out there.");
      onAcknowledged?.(response);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to acknowledge"));
    }
    setBusy(null);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
      data-testid="status-check-modal"
    >
      <div className="w-full max-w-md border-2 border-[#FF9F0A] bg-[#0F0F0F]">
        <div className="border-b border-[#27272A] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-[#FF9F0A]" />
            <div>
              <div className="label-mute text-[#FF9F0A]">Status Check</div>
              <div className="font-display uppercase text-lg font-bold tracking-tight">
                Confirm You&rsquo;re Safe
              </div>
            </div>
          </div>
          <div
            className={`font-mono text-2xl font-black ${isCritical ? "text-[#FF3B30]" : "text-[#FF9F0A]"}`}
            data-testid="status-check-countdown"
          >
            {String(Math.floor(remainingSec / 60)).padStart(2, "0")}:
            {String(remainingSec % 60).padStart(2, "0")}
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-300">
            An automated check-in was scheduled for your shift. Please confirm your
            status. Missed check-ins are logged and may trigger a supervisor
            welfare check.
          </p>

          <textarea
            data-testid="status-check-note"
            placeholder="Optional note — where you are, what's happening…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full bg-[#0A0A0A] border border-[#27272A] text-sm p-2 focus:border-[#FF9F0A] outline-none"
            maxLength={500}
          />

          <div className="grid grid-cols-1 gap-2">
            <ActionButton
              data-testid="status-check-ok"
              disabled={busy != null}
              onClick={() => ack("ok")}
              className="w-full !bg-[#34C759] hover:!bg-[#2FB84D] !text-black"
            >
              <ShieldCheck className="size-4" /> I&rsquo;m OK — All Good
            </ActionButton>
            <GhostButton
              data-testid="status-check-investigating"
              disabled={busy != null}
              onClick={() => ack("investigating")}
              className="w-full !border-[#FF9F0A] !text-[#FF9F0A]"
            >
              <AlertTriangle className="size-4" /> Investigating Something
            </GhostButton>
            <GhostButton
              data-testid="status-check-need-backup"
              disabled={busy != null}
              onClick={() => ack("need_backup")}
              className="w-full !border-[#FF3B30] !text-[#FF3B30]"
            >
              <Users className="size-4" /> Need Backup — Alert Supervisor
            </GhostButton>
          </div>
        </div>
      </div>
    </div>
  );
}
